// xmlHandling.ts
// XML-Parsing und -Serialisierung für Rules-Import/Export

import {
  Adjustment,
  ConditionGroup,
  Creation,
  DataModel,
  Dimension,
  DimensionType,
  Fixation,
  Metric,
  Objective,
  Point,
  Relation,
  Settings,
  Tag,
  Timing,
  Visual,
  Capacity,
  RelationTarget,
} from "./AppTypes";
import { ExtendedCondition } from "./ConditionEditor";

/**
 * Escapt einen String für die Verwendung in XML-Attributwerten.
 * Wichtig: Das ist NICHT dasselbe wie `sanitizeXmlName`.
 * - sanitize = Daten verändern/ersetzen (für alte/kaputte Inputs)
 * - escape   = XML-sicher machen, ohne Information zu verlieren
 */
function escapeXmlAttr(value: string): string {
  return value
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/\"/g, "&quot;")
    .replace(/'/g, "&apos;");
}

/**
 * Normalisiert XML-Attributwerte beim Import.
 * DOMParser liefert bereits dekodierte Werte (z.B. &amp; -> &).
 * Wir entfernen nur nicht erlaubte Steuerzeichen, lassen aber normale Zeichen
 * (inkl. <, >, &, ") unverändert, damit Name-Matching funktioniert.
 */
function normalizeXmlAttr(value: string): string {
  return value
    .split("")
    .map((ch) => {
      const code = ch.charCodeAt(0);
      if (code < 32 && code !== 9 && code !== 10 && code !== 13) return " ";
      return ch;
    })
    .join("");
}

function parseNumberAttr(value: string | null | undefined, fallback: number): number {
  if (value === null || value === undefined || value === "") return fallback;
  const n = Number(value);
  return Number.isFinite(n) ? n : fallback;
}

function parseTrueAttr(value: string | null | undefined): boolean {
  return (value || "").trim().toLowerCase() === "true";
}

/**
 * Macht einen String "XML-sicher" für Attributwerte:
 * - unerlaubte Steuerzeichen (<32, außer TAB, LF, CR) werden zu "_"
 * - problematische Zeichen wie <, >, &, ", , werden zu "_"
 *
 * So können z.B. Namen in XML-Attributen verwendet werden,
 * ohne die Struktur des Dokuments zu zerstören.
 */
export function sanitizeXmlName(input: string): string {
  if (!input) return "_";
  return input
    .split("")
    .map((ch) => {
      const code = ch.charCodeAt(0);

      // verbotene Steuerzeichen in XML (mit Ausnahmen für Tab, LF, CR)
      if (code < 32 && code !== 9 && code !== 10 && code !== 13) return "_";

      // typische Sonderzeichen, die XML kaputt machen könnten
      if (ch === "<" || ch === ">" || ch === "&" || ch === "," || ch === '"')
        return "_";

      return ch;
    })
    .join("");
}

/**
 * Liest Visual-Attribute aus einem XML-Element.
 * Unterstützt folgende Attribute:
 * - lightness="..."
 * - chroma="..."
 * - hue="..."
 *
 * Falls keins davon vorhanden ist, wird { type: "default" } zurückgegeben.
 */
export function parseVisual(el: Element): Visual {
  if (el.hasAttribute("lightness")) {
    return {
      type: "lightness",
      value: parseNumberAttr(el.getAttribute("lightness"), 0),
    };
  }
  if (el.hasAttribute("chroma")) {
    return {
      type: "chroma",
      value: parseNumberAttr(el.getAttribute("chroma"), 0),
    };
  }
  if (el.hasAttribute("hue")) {
    return { type: "hue", value: parseNumberAttr(el.getAttribute("hue"), 0) };
  }
  return { type: "default" };
}

/**
 * Einfache ID-Hilfsfunktion für importierte Objekte:
 * - kombiniert ein Prefix (z.B. "dim") mit Zeitstempel + Zufallsstring
 * => ergibt eine ziemlich eindeutige ID, z.B. dim-lmno12-abc123
 */
export function makeId(prefix: string): string {
  const rand = Math.random().toString(36).substring(2, 8); // Zufall
  const time = Date.now().toString(36);                     // Zeit
  return `${prefix}-${time}-${rand}`;
}

/**
 * DataModel → XML (string) für Export
 *
 * Serialisiert:
 * - settings
 * - dimensions (inkl. tags, points + tagRefs)
 * - relations (inkl. targets)
 * - conditions (inkl. dimrefs, pattern, group)
 *
 * Ausgabe-Struktur:
 * <zeitgeist>
 *   <settings ... />
 *   <dimensions>...</dimensions>
 *   <relations>...</relations>
 *   <conditions>...</conditions>
 * </zeitgeist>
 */
export function xmlFromData(data: DataModel): string {
  const { settings, dimensions, timings = [], relations, creations, capacities, conditions, conditionGroups, metrics = [] } = data;

  let xml = `<?xml version="1.0" encoding="UTF-8"?>\n<zeitgeist>\n`;

  // ---------- Settings ----------
  xml += `  <settings\n`;

  // Reihenfolge der Settings-Attribute explizit festgelegt
  const orderedKeys: (keyof typeof settings)[] = [
    "version",
    "hue",
    "language",
    "region",
    "state",
    "calendar",
    "timezone",
  ];

  // Alle Settings als Attribute an <settings ... />
  orderedKeys.forEach((key) => {
    xml += `    ${key}="${escapeXmlAttr(String(settings[key]))}"\n`;
  });
  xml += `  />\n`;

  // ---------- Dimensions ----------
  xml += `  <dimensions>\n`;
  dimensions.forEach((d) => {
    const typeAttr = d.dimType && d.dimType !== "none" ? ` type="${d.dimType}"` : "";
    const startDateAttr   = d.dimType === "time.period" && d.startDate   ? ` start_date="${escapeXmlAttr(d.startDate)}"` : "";
    const endDateAttr     = d.dimType === "time.period" && d.endDate     ? ` end_date="${escapeXmlAttr(d.endDate)}"` : "";
    const numberOfDaysAttr = d.dimType === "time.period" ? ` number_of_days="${d.numberOfDays ?? 7}"` : "";
    const lookaheadAttr   = d.dimType === "time.period" && d.lookahead != null ? ` lookahead="${d.lookahead}"` : "";
    const publishAttr     = d.dimType === "time.period" && d.publish != null   ? ` publish="${d.publish}"` : "";
    const numberOfLanesAttr = d.dimType === "time.slot" && d.calendarMaxLanes != null ? ` number_of_lanes="${d.calendarMaxLanes}"` : "";
    xml += `    <dimension name="${escapeXmlAttr(d.name)}"${typeAttr}${startDateAttr}${endDateAttr}${numberOfDaysAttr}${lookaheadAttr}${publishAttr}${numberOfLanesAttr}>\n`;

    // Tags einer Dimension
    d.tags.forEach((t) => {
      let visualAttrs = "";
      if (t.visual.type === "lightness")
        visualAttrs = ` lightness="${t.visual.value}"`;
      if (t.visual.type === "chroma")
        visualAttrs = ` chroma="${t.visual.value}"`;
      if (t.visual.type === "hue") visualAttrs = ` hue="${t.visual.value}"`;
      const fixAttr = "";

      xml += `      <tag name="${escapeXmlAttr(t.name)}"${visualAttrs}${fixAttr} />\n`;
    });

    // Points einer Dimension + deren Tag-Referenzen
    d.points.forEach((p) => {
      let visualAttrs = "";
      if (p.visual.type === "lightness")
        visualAttrs = ` lightness="${p.visual.value}"`;
      if (p.visual.type === "chroma")
        visualAttrs = ` chroma="${p.visual.value}"`;
      if (p.visual.type === "hue") visualAttrs = ` hue="${p.visual.value}"`;
      const fixAttr = "";
    const pointStartDateAttr = d.dimType === "time.period" && p.startDate ? ` start_date="${escapeXmlAttr(p.startDate)}"` : "";
    const pointTemplateAttr  = d.dimType === "time.period" && p.template  ? ` template="true"` : "";
  const pointDayOfPeriodAttr = d.dimType === "time.day" && p.dayOfPeriod != null ? ` day_of_period="${p.dayOfPeriod}"` : "";
      const pointStartTimeAttr     = d.dimType === "time.slot" && p.startTime        ? ` start_time="${escapeXmlAttr(p.startTime)}"` : "";
      const pointDurationMinAttr   = d.dimType === "time.slot" && p.durationMinutes != null ? ` duration_minutes="${p.durationMinutes}"` : "";

      if (p.tagRefs.length === 0) {
        xml += `      <point name="${escapeXmlAttr(p.name)}"${visualAttrs}${fixAttr}${pointStartDateAttr}${pointTemplateAttr}${pointDayOfPeriodAttr}${pointStartTimeAttr}${pointDurationMinAttr} />\n`;
      } else {
        xml += `      <point name="${escapeXmlAttr(p.name)}"${visualAttrs}${fixAttr}${pointStartDateAttr}${pointTemplateAttr}${pointDayOfPeriodAttr}${pointStartTimeAttr}${pointDurationMinAttr}>\n`;

        // tagRefs in namenbasierte <tagref name="..."/> umwandeln
        p.tagRefs.forEach((refId) => {
          const tag = d.tags.find((t) => t.id === refId);
          if (tag) {
            xml += `        <tagref name="${escapeXmlAttr(tag.name)}" />\n`;
          }
        });

        xml += `      </point>\n`;
      }
    });

    xml += `    </dimension>\n`;
  });
  xml += `  </dimensions>\n`;

  // ---------- Timings ----------
  if (timings.length > 0) {
    xml += `  <timings>\n`;
    timings.forEach((t) => {
      if (t.type === "relative") {
        const anchorAttr  = t.anchor    ? ` anchor="${escapeXmlAttr(t.anchor)}"`       : "";
        const offsetAttr  = t.offset != null ? ` offset="${t.offset}"`                : "";
        const dirAttr     = t.direction ? ` direction="${escapeXmlAttr(t.direction)}"` : "";
        const lengthAttr  = t.length    ? ` length="${escapeXmlAttr(t.length)}"`       : "";
        const unitAttr    = t.unit      ? ` unit="${escapeXmlAttr(t.unit)}"`           : "";
        xml += `    <timing name="${escapeXmlAttr(t.name)}" type="relative"${anchorAttr}${offsetAttr}${dirAttr}${lengthAttr}${unitAttr} />\n`;
      } else {
        const startAttr = t.start_date ? ` start_date="${escapeXmlAttr(t.start_date)}"` : "";
        const endAttr   = t.end_date   ? ` end_date="${escapeXmlAttr(t.end_date)}"`     : "";
        xml += `    <timing name="${escapeXmlAttr(t.name)}" type="absolute"${startAttr}${endAttr} />\n`;
      }
    });
    xml += `  </timings>\n`;
  }

  // ---------- Relations ----------
  xml += `  <relations>\n`;
  relations.forEach((r) => {
    const scopeAttr = (r as any).scope && (r as any).scope !== "always" ? ` scope="${(r as any).scope}"` : "";
    const modeAttr  = (r as any).mode  && (r as any).mode  !== "any"    ? ` mode="${(r as any).mode}"`   : "";
    xml += `    <relation type="${r.type}" name="${escapeXmlAttr(r.name)}"${scopeAttr}${modeAttr}>\n`;

    // jede Relation hat mehrere Targets (dimref)
    r.targets.forEach((t) => {
      const dimObj: Dimension | undefined = dimensions.find(
        (d) => d.id === t.dimensionId
      );
      const dimName = dimObj ? escapeXmlAttr(dimObj.name) : "_";

      xml += `      <dimref name="${dimName}">\n`;

      if (t.mode === "all") {
        // Alle Punkte in der Dimension
        xml += `        <allpoints />\n`;
      } else if (t.mode === "tags") {
        // ausgewählte Tags referenzieren
        t.tagIds.forEach((tagId) => {
          const tag = dimObj?.tags.find((tg) => tg.id === tagId);
          if (tag) {
            xml += `        <tagref name="${escapeXmlAttr(tag.name)}" />\n`;
          }
        });
      } else if (t.mode === "points") {
        // ausgewählte Punkte referenzieren
        t.pointIds.forEach((pointId) => {
          const pt = dimObj?.points.find((p) => p.id === pointId);
          if (pt) {
            xml += `        <pointref name="${escapeXmlAttr(pt.name)}" />\n`;
          }
        });
      }

      xml += `      </dimref>\n`;
    });

    xml += `    </relation>\n`;
  });
  xml += `  </relations>\n`;

  // ---------- Creations ----------
  xml += `  <creations>\n`;
  (creations || []).forEach((creation) => {
    const targetAttr = creation.type === "maximize" ? "maximize" : "equal";
    xml += `    <creation name="${escapeXmlAttr(creation.name)}" ${targetAttr}="${creation.equalValue}" enforcement="${creation.enforcement}" connect="${creation.connect ? "true" : "false"}">\n`;

    const appendTargetList = (role: "make" | "for", targets: RelationTarget[]) => {
      targets.forEach((t) => {
        const dimObj = dimensions.find((d) => d.id === t.dimensionId);
        if (!dimObj) return;
        xml += `      <dimref name="${escapeXmlAttr(dimObj.name)}" role="${role}">\n`;
        if (t.mode === "all") {
          xml += `        <allpoints />\n`;
        } else if (t.mode === "tags") {
          t.tagIds.forEach((tagId) => {
            const tag = dimObj.tags.find((tg) => tg.id === tagId);
            if (tag) xml += `        <tagref name="${escapeXmlAttr(tag.name)}" />\n`;
          });
        } else {
          t.pointIds.forEach((pointId) => {
            const point = dimObj.points.find((pt) => pt.id === pointId);
            if (point) xml += `        <pointref name="${escapeXmlAttr(point.name)}" />\n`;
          });
        }
        xml += `      </dimref>\n`;
      });
    };

    appendTargetList("make", creation.make || []);
    appendTargetList("for", creation.for || []);
    xml += `    </creation>\n`;
  });
  xml += `  </creations>\n`;

  // ---------- Capacities ----------
  xml += `  <capacities>\n`;
  (capacities || []).forEach((c) => {
    const scopeAttr = (c as any).scope && (c as any).scope !== "always" ? ` scope="${(c as any).scope}"` : "";
    const modeAttr  = (c as any).mode  && (c as any).mode  !== "any"    ? ` mode="${(c as any).mode}"`   : "";
    xml += `    <capacity name="${escapeXmlAttr(c.name)}" limit="${c.limitValue}"${scopeAttr}${modeAttr}>\n`;

    const writeTarget = (t: RelationTarget, role: string) => {
      const dimObj: Dimension | undefined = dimensions.find((d) => d.id === t.dimensionId);
      const dimName = dimObj ? escapeXmlAttr(dimObj.name) : "_";
      xml += `      <dimref name="${dimName}" role="${role}">\n`;
      if (t.mode === "all") xml += `        <allpoints />\n`;
      else if (t.mode === "tags") {
        t.tagIds.forEach((tagId) => {
          const tag = dimObj?.tags.find((tg) => tg.id === tagId);
          if (tag) xml += `        <tagref name="${escapeXmlAttr(tag.name)}" />\n`;
        });
      } else if (t.mode === "points") {
        t.pointIds.forEach((pointId) => {
          const pt = dimObj?.points.find((p) => p.id === pointId);
          if (pt) xml += `        <pointref name="${escapeXmlAttr(pt.name)}" />\n`;
        });
      }
      xml += `      </dimref>\n`;
    };

    c.for.forEach((t) => writeTarget(t, "for"));
    c.where.forEach((t) => writeTarget(t, "where"));
    xml += `    </capacity>\n`;
  });
  xml += `  </capacities>\n`;

  // ---------- Conditions ----------
  xml += `  <conditions>\n`;
  conditions.forEach((c) => {
    // Basis-Attribute: type + name
    let attrs = `type="${c.type}" name="${escapeXmlAttr(c.name)}"`;

    // enforcement (falls vorhanden)
    if ("enforcement" in c && (c as any).enforcement) {
      attrs += ` enforcement="${(c as any).enforcement}"`;
    }

    // Gruppen-Name hinzufügen, falls Condition einer Gruppe zugeordnet ist
    if ((c as any).groupId && conditionGroups) {
      const group = conditionGroups.find((g) => g.id === (c as any).groupId);
      if (group) {
        attrs += ` group="${escapeXmlAttr(group.name)}"`;
      }
    }

    // Pattern-Information (z.B. equal="5" oder range="1,3")
    if ((c as any).patternType && (c as any).patternValue) {
      attrs += ` ${(c as any).patternType}="${escapeXmlAttr(String((c as any).patternValue))}"`;
    }

    if ((c as any).time_ref && (c as any).type !== "follow") {
      attrs += ` time_ref="${escapeXmlAttr(String((c as any).time_ref))}"`;
    }

    // scope (nur wenn nicht "always")
    if ((c as any).scope && (c as any).scope !== "always") {
      attrs += ` scope="${(c as any).scope}"`;
    }

    // mode (nur wenn nicht "any")
    if ((c as any).mode && (c as any).mode !== "any") {
      attrs += ` mode="${(c as any).mode}"`;
    }

    xml += `    <condition ${attrs}>\n`;

    // --- align condition export ---
    if ((c as any).type === "align") {
      const participants = (c as any).participants || [];
      const on = (c as any).on || { dimrefs: [] };

      const writeDimRef = (dr: any, indent: string, forceRole?: string, noRole?: boolean) => {
        const dim = dimensions.find((d) => d.id === dr.dimensionId);
        if (!dim) return;
        const role = forceRole ?? (noRole ? undefined : dr.role);
        const roleAttr = role ? ` role="${role}"` : "";
        xml += `${indent}<dimref name="${escapeXmlAttr(dim.name)}"${roleAttr}>\n`;
        if (dr.mode === "all") {
          xml += `${indent}  <allpoints />\n`;
        } else if (dr.mode === "tags") {
          (dr.tagIds || []).forEach((tid: string) => {
            const tag = dim.tags.find((t) => t.id === tid);
            if (tag) xml += `${indent}  <tagref name="${escapeXmlAttr(tag.name)}" />\n`;
          });
        } else if (dr.mode === "points") {
          (dr.pointIds || []).forEach((pid: string) => {
            const pt = dim.points.find((p) => p.id === pid);
            if (pt) xml += `${indent}  <pointref name="${escapeXmlAttr(pt.name)}" />\n`;
          });
        }
        xml += `${indent}</dimref>\n`;
      };

      xml += `      <participants>\n`;
      participants.forEach((p: any) => {
        xml += `        <participant>\n`;
        (p.dimrefs || []).forEach((dr: any) => writeDimRef(dr, "          ", undefined, true));
        xml += `        </participant>\n`;
      });
      xml += `      </participants>\n`;

      // <on> dimrefs werden flach als <dimref role="on"> direkt unter <condition> geschrieben
      (on.dimrefs || []).forEach((dr: any) => writeDimRef(dr, "      ", "on"));
    } else if ((c as any).type === "follow") {
      // --- follow condition export: metric_ref IF/THEN + normal dimrefs ---
      const metricRefIf = (c as any).metricRefIf;
      const metricRefThen = (c as any).metricRefThen;

      // dimrefs (for/where) exportieren
      if ((c as any).dimrefs) {
        const roleOrder: Record<string, number> = { for: 0, where: 1 };
        const sortedDimrefs = [...(c as any).dimrefs].sort((a: any, b: any) => {
          const aOrder = roleOrder[a.role ?? "for"] ?? 0;
          const bOrder = roleOrder[b.role ?? "for"] ?? 0;
          return aOrder - bOrder;
        });
        sortedDimrefs.forEach((dr: any) => {
          const dim = dimensions.find((d) => d.id === dr.dimensionId);
          if (!dim) return;
          const roleAttr = dr.role ? ` role="${dr.role}"` : "";
          xml += `      <dimref name="${escapeXmlAttr(dim.name)}"${roleAttr}>\n`;
          if (dr.mode === "all") {
            xml += `        <allpoints />\n`;
          } else if (dr.mode === "tags") {
            dr.tagIds.forEach((tid: string) => {
              const tag = dim.tags.find((t) => t.id === tid);
              if (tag) xml += `        <tagref name="${escapeXmlAttr(tag.name)}" />\n`;
            });
          } else if (dr.mode === "points") {
            dr.pointIds.forEach((pid: string) => {
              const pt = dim.points.find((p) => p.id === pid);
              if (pt) xml += `        <pointref name="${escapeXmlAttr(pt.name)}" />\n`;
            });
          }
          xml += `      </dimref>\n`;
        });
      }

      // metric_ref IF
      if (metricRefIf && metricRefIf.name) {
        const patternAttr = metricRefIf.patternValue ? ` ${metricRefIf.patternType}="${escapeXmlAttr(metricRefIf.patternValue)}"` : "";
        xml += `      <metric_ref role="if" name="${escapeXmlAttr(metricRefIf.name)}"${patternAttr} />\n`;
      }
      // metric_ref THEN
      if (metricRefThen && metricRefThen.name) {
        const patternAttr = metricRefThen.patternValue ? ` ${metricRefThen.patternType}="${escapeXmlAttr(metricRefThen.patternValue)}"` : "";
        xml += `      <metric_ref role="then" name="${escapeXmlAttr(metricRefThen.name)}"${patternAttr} />\n`;
      }
    } else {
      // dimrefs innerhalb einer Condition exportieren (Reihenfolge: for → on → where)
      if ((c as any).dimrefs) {
        const roleOrder: Record<string, number> = { for: 0, on: 1, where: 2 };
        const sortedDimrefs = [...(c as any).dimrefs].sort((a: any, b: any) => {
          const aOrder = roleOrder[a.role ?? "for"] ?? 0;
          const bOrder = roleOrder[b.role ?? "for"] ?? 0;
          return aOrder - bOrder;
        });
        sortedDimrefs.forEach((dr: any) => {
          const dim = dimensions.find((d) => d.id === dr.dimensionId);
          if (!dim) return;

          const roleAttr = dr.role ? ` role="${dr.role}"` : "";
          xml += `      <dimref name="${escapeXmlAttr(dim.name)}"${roleAttr}>\n`;

          if (dr.mode === "all") {
            xml += `        <allpoints />\n`;
          } else if (dr.mode === "tags") {
            dr.tagIds.forEach((tid: string) => {
              const tag = dim.tags.find((t) => t.id === tid);
              if (tag) {
                xml += `        <tagref name="${escapeXmlAttr(tag.name)}" />\n`;
              }
            });
          } else if (dr.mode === "points") {
            dr.pointIds.forEach((pid: string) => {
              const pt = dim.points.find((p) => p.id === pid);
              if (pt) {
                xml += `        <pointref name="${escapeXmlAttr(pt.name)}" />\n`;
              }
            });
          }
          xml += `      </dimref>\n`;
        });
      }
    }

    xml += `    </condition>\n`;
  });
  xml += `  </conditions>\n`;

  // ---------- Metrics ----------
  if (metrics.length > 0) {
    xml += `  <metrics>\n`;
    metrics.forEach((metric) => {
      let attrs = `name="${escapeXmlAttr(metric.name)}" type="${escapeXmlAttr(metric.type)}"`;
      if (metric.time_ref) {
        attrs += ` time_ref="${escapeXmlAttr(metric.time_ref)}"`;
      }
      xml += `    <metric ${attrs}>\n`;

      (metric.dimrefs || []).forEach((dr) => {
        const dim = dimensions.find((d) => d.id === dr.dimensionId);
        if (!dim) return;
        xml += `      <dimref name="${escapeXmlAttr(dim.name)}" role="${dr.role}">\n`;
        if (dr.mode === "all") {
          xml += `        <allpoints />\n`;
        } else if (dr.mode === "tags") {
          dr.tagIds.forEach((tid) => {
            const tag = dim.tags.find((t) => t.id === tid);
            if (tag) xml += `        <tagref name="${escapeXmlAttr(tag.name)}" />\n`;
          });
        } else if (dr.mode === "points") {
          dr.pointIds.forEach((pid) => {
            const pt = dim.points.find((p) => p.id === pid);
            if (pt) xml += `        <pointref name="${escapeXmlAttr(pt.name)}" />\n`;
          });
        }
        xml += `      </dimref>\n`;
      });

      xml += `    </metric>\n`;
    });
    xml += `  </metrics>\n`;
  }

  // ---------- Objectives ----------
  const objectives = data.objectives ?? [];
  if (objectives.length > 0) {
    xml += `  <objectives>\n`;
    objectives.forEach((obj) => {
      let attrs = `name="${escapeXmlAttr(obj.name)}" type="${escapeXmlAttr(obj.type)}" metric_ref="${escapeXmlAttr(obj.metric_ref)}"`;
      if (obj.type === "target" && obj.target !== undefined) {
        attrs += ` target="${escapeXmlAttr(String(obj.target))}"`;
      }
      xml += `    <objective ${attrs} />\n`;
    });
    xml += `  </objectives>\n`;
  }

  // ---------- Fixations ----------
  const fixations = data.fixations ?? [];
  if (fixations.length > 0) {
    xml += `  <fixations>\n`;
    fixations.forEach((f) => {
      xml += `    <fixation name="${escapeXmlAttr(f.name)}">\n`;
      f.targets.forEach((t) => {
        const d = dimensions.find((dim) => dim.id === t.dimensionId);
        if (!d) return;
        xml += `      <dimref name="${escapeXmlAttr(d.name)}">\n`;
        if (t.mode === "all") {
          xml += `        <allpoints />\n`;
        } else if (t.mode === "tags") {
          t.tagIds.forEach((tid) => {
            const tag = d.tags.find((tg) => tg.id === tid);
            if (tag) xml += `        <tagref name="${escapeXmlAttr(tag.name)}" />\n`;
          });
        } else if (t.mode === "points") {
          t.pointIds.forEach((pid) => {
            const pt = d.points.find((p) => p.id === pid);
            if (pt) xml += `        <pointref name="${escapeXmlAttr(pt.name)}" />\n`;
          });
        }
        xml += `      </dimref>\n`;
      });
      xml += `    </fixation>\n`;
    });
    xml += `  </fixations>\n`;
  }

  // ---------- Adjustments ----------
  const adjustments = data.adjustments ?? [];
  if (adjustments.length > 0) {
    xml += `  <adjustments>\n`;
    adjustments.forEach((a) => {      const dimObj = (dimId: string) => dimensions.find((d) => d.id === dimId);
      // Datumsattribute je nach type
      const dateAttrs = a.type === "off"
        ? `${a.startDate ? ` start_date="${escapeXmlAttr(a.startDate)}"` : ""}${a.endDate ? ` end_date="${escapeXmlAttr(a.endDate)}"` : ""}`
        : `${a.date ? ` date="${escapeXmlAttr(a.date)}"` : ""}`;
      xml += `    <adjustment type="${a.type}" name="${escapeXmlAttr(a.name)}"${dateAttrs}>\n`;
      a.targets.forEach((t) => {
        const d = dimObj(t.dimensionId);
        if (!d) return;
        xml += `      <dimref name="${escapeXmlAttr(d.name)}">\n`;
        if (t.mode === "all") {
          xml += `        <allpoints />\n`;
        } else if (t.mode === "tags") {
          t.tagIds.forEach((tid) => {
            const tag = d.tags.find((tg) => tg.id === tid);
            if (tag) xml += `        <tagref name="${escapeXmlAttr(tag.name)}" />\n`;
          });
        } else if (t.mode === "points") {
          t.pointIds.forEach((pid) => {
            const pt = d.points.find((p) => p.id === pid);
            if (pt) xml += `        <pointref name="${escapeXmlAttr(pt.name)}" />\n`;
          });
        }
        xml += `      </dimref>\n`;
      });
      xml += `    </adjustment>\n`;
    });
    xml += `  </adjustments>\n`;
  }

  xml += `</zeitgeist>\n`;
  return xml;
}

/**
 * XML → DataModel für Import
 *
 * Liest ein XML-String ein und versucht, daraus ein DataModel zu
 * rekonstruieren:
 * - Settings
 * - Dimensions (inkl. Tags und Points)
 * - Relations (inkl. Targets)
 * - Conditions (inkl. dimrefs und Gruppen)
 *
 * Bei Fehlern (Parsing, Struktur) wird null zurückgegeben.
 */
export function xmlToData(text: string): DataModel | null {
  try {
    const parser = new DOMParser();
    const doc = parser.parseFromString(text, "application/xml");

    // Prüfen, ob es einen Parser-Fehler gibt
    const parseError = doc.querySelector("parsererror");
    if (parseError) {
      console.error("XML parse error:", parseError.textContent);
      return null;
    }

    /* === Settings === */
    const settingsNode = doc.querySelector("settings");
    const settings: Settings = {
      version: normalizeXmlAttr(settingsNode?.getAttribute("version") || "0.1"),
      hue: parseNumberAttr(settingsNode?.getAttribute("hue"), 220),
      language: normalizeXmlAttr(settingsNode?.getAttribute("language") || "de"),
      region: normalizeXmlAttr(settingsNode?.getAttribute("region") || "DE"),
      state: normalizeXmlAttr(settingsNode?.getAttribute("state") || "NI"),
      calendar: normalizeXmlAttr(
        settingsNode?.getAttribute("calendar") || "iso8601"
      ),
      timezone: normalizeXmlAttr(
        settingsNode?.getAttribute("timezone") || "Europe/Berlin"
      ),
    };

    /* === Dimensions === */
    const dimensions: Dimension[] = [];

    // alle <dimension>-Elemente unter <dimensions>
    doc.querySelectorAll("dimensions > dimension").forEach((d) => {
      const dimId = makeId("dim");

      // Tags innerhalb der Dimension
      const tags: Tag[] = [];
      d.querySelectorAll(":scope > tag").forEach((t) => {
        tags.push({
          id: makeId("tag"),
          name: normalizeXmlAttr(t.getAttribute("name") || ""),
          visual: parseVisual(t),
        });
      });

      // Points innerhalb der Dimension (erstmal mit tagRefs als Namen)
      const points: Point[] = [];
      d.querySelectorAll(":scope > point").forEach((p) => {
        const pointStartDate = p.getAttribute("start_date");
        const pointDayOfPeriodRaw = p.getAttribute("day_of_period") ?? p.getAttribute("day_of_week");
        const pointDayOfPeriod = pointDayOfPeriodRaw != null ? parseInt(pointDayOfPeriodRaw, 10) : undefined;
        const pointStartTime = p.getAttribute("start_time");
        const pointDurationMinRaw = p.getAttribute("duration_minutes");
        const pointEndTime   = p.getAttribute("end_time"); // Legacy-Kompatibilität
        const pointTemplate  = p.getAttribute("template");
        // duration_minutes aus Attribut lesen; Legacy: end_time → duration_minutes berechnen
        let resolvedDurationMinutes: number | undefined;
        if (pointDurationMinRaw != null) {
          const parsed = parseInt(pointDurationMinRaw, 10);
          if (!isNaN(parsed)) resolvedDurationMinutes = parsed;
        } else if (pointEndTime && pointStartTime) {
          // Fallback: end_time - start_time in Minuten umrechnen
          const [sh, sm] = pointStartTime.split(":").map(Number);
          const [eh, em] = pointEndTime.split(":").map(Number);
          const diff = (eh * 60 + em) - (sh * 60 + sm);
          if (!isNaN(diff) && diff > 0) resolvedDurationMinutes = diff;
        }
        points.push({
          id: makeId("point"),
          name: normalizeXmlAttr(p.getAttribute("name") || ""),
          visual: parseVisual(p),
          tagRefs: Array.from(p.querySelectorAll("tagref")).map((tr) =>
            normalizeXmlAttr(tr.getAttribute("name") || "")
          ),
          ...(pointStartDate ? { startDate: normalizeXmlAttr(pointStartDate) } : {}),
          ...(pointDayOfPeriod != null && !isNaN(pointDayOfPeriod) ? { dayOfPeriod: pointDayOfPeriod } : {}),
          ...(pointStartTime ? { startTime: normalizeXmlAttr(pointStartTime) } : {}),
          ...(resolvedDurationMinutes != null ? { durationMinutes: resolvedDurationMinutes } : {}),
          ...(pointTemplate === "true" ? { template: true } : {}),
        });
      });

      const rawType = d.getAttribute("type");
      // Backward compatibility: alte XMLs mit time.week werden intern als time.period behandelt
      const normalizedRawType = rawType === "time.week" ? "time.period" : rawType;
      const dimTypeValues: DimensionType[] = ["none", "time.day", "time.slot", "time.period", "resource.team", "resource.person", "resource.place", "resource.asset", "topic"];
      const dimType: DimensionType = (normalizedRawType && dimTypeValues.includes(normalizedRawType as DimensionType))
        ? (normalizedRawType as DimensionType)
        : "none";

      dimensions.push({
        id: dimId,
        name: normalizeXmlAttr(d.getAttribute("name") || ""),
        dimType,
        tags,
        points,
        ...(dimType === "time.period" && d.getAttribute("start_date")
          ? { startDate: normalizeXmlAttr(d.getAttribute("start_date") || "") }
          : {}),
        ...(dimType === "time.period" && d.getAttribute("end_date")
          ? { endDate: normalizeXmlAttr(d.getAttribute("end_date") || "") }
          : {}),
        ...(dimType === "time.period"
          ? {
              numberOfDays:
                d.getAttribute("number_of_days") != null && d.getAttribute("number_of_days") !== ""
                  ? parseInt(d.getAttribute("number_of_days") || "7", 10)
                  : 7,
            }
          : {}),
        ...(dimType === "time.period" && d.getAttribute("lookahead") != null && d.getAttribute("lookahead") !== ""
          ? { lookahead: parseInt(d.getAttribute("lookahead") || "0", 10) }
          : {}),
        ...(dimType === "time.period" && d.getAttribute("publish") != null && d.getAttribute("publish") !== ""
          ? { publish: parseInt(d.getAttribute("publish") || "0", 10) }
          : {}),
        ...(dimType === "time.slot" && d.getAttribute("number_of_lanes") != null && d.getAttribute("number_of_lanes") !== ""
          ? { calendarMaxLanes: Math.max(1, parseInt(d.getAttribute("number_of_lanes") || "1", 10)) }
          : {}),
      });
    });

    /* === Relations === */
    const relations: Relation[] = [];
    doc.querySelectorAll("relations > relation").forEach((r, i) => {
      const targets = Array.from(r.querySelectorAll("dimref")).map((dr) => {
        const dimName = normalizeXmlAttr(dr.getAttribute("name") || "");
        const dim = dimensions.find((d) => d.name === dimName);

        // falls Dimension nicht gefunden wird, Dummy-Target zurückgeben
        if (!dim) {
          return {
            dimensionId: "",
            mode: "all" as const,
            tagIds: [],
            pointIds: [],
          };
        }

        // "allpoints" -> Mode "all"
        if (dr.querySelector("allpoints")) {
          return {
            dimensionId: dim.id,
            mode: "all" as const,
            tagIds: [],
            pointIds: [],
          };
        }

        // TagRefs anhand Namen → Tag-IDs
        const tagIds = Array.from(dr.querySelectorAll("tagref"))
          .map((t) => normalizeXmlAttr(t.getAttribute("name") || ""))
          .map((n) => dim.tags.find((tg) => tg.name === n)?.id || "")
          .filter(Boolean);

        // PointRefs anhand Namen → Point-IDs
        const pointIds = Array.from(dr.querySelectorAll("pointref"))
          .map((p) => normalizeXmlAttr(p.getAttribute("name") || ""))
          .map((n) => dim.points.find((pt) => pt.name === n)?.id || "")
          .filter(Boolean);

        // Wenn nur Tags gesetzt sind → mode "tags"
        if (tagIds.length && !pointIds.length) {
          return {
            dimensionId: dim.id,
            mode: "tags" as const,
            tagIds,
            pointIds: [],
          };
        }

        // Wenn nur Points gesetzt sind → mode "points"
        if (pointIds.length && !tagIds.length) {
          return {
            dimensionId: dim.id,
            mode: "points" as const,
            tagIds: [],
            pointIds,
          };
        }

        // Fallback: "all"
        return {
          dimensionId: dim.id,
          mode: "all" as const,
          tagIds: [],
          pointIds: [],
        };
      });

      relations.push({
        id: makeId("rel"),
        name: normalizeXmlAttr(r.getAttribute("name") || `Relation${i + 1}`),
        type:
          (r.getAttribute("type") as "connection" | "disconnection") ||
          "connection",
        targets,
        ...(r.getAttribute("scope") ? { scope: r.getAttribute("scope") } : {}),
        ...(r.getAttribute("mode")  ? { mode:  r.getAttribute("mode")  } : {}),
      } as any);
    });

    /* === Creations === */
    const creations: Creation[] = [];
    doc.querySelectorAll("creations > creation").forEach((c, i) => {
      const parseDimRefs = (dimRefs: Element[]): RelationTarget[] => {
        return dimRefs.map((dr) => {
          const dimName = normalizeXmlAttr(dr.getAttribute("name") || "");
          const dim = dimensions.find((d) => d.name === dimName);
          if (!dim) return { dimensionId: "", mode: "all" as const, tagIds: [], pointIds: [] };

          if (dr.querySelector("allpoints")) return { dimensionId: dim.id, mode: "all" as const, tagIds: [], pointIds: [] };

          const tagIds = Array.from(dr.querySelectorAll("tagref"))
            .map((t) => normalizeXmlAttr(t.getAttribute("name") || ""))
            .map((n) => dim.tags.find((tg) => tg.name === n)?.id || "").filter((id): id is string => Boolean(id));

          const pointIds = Array.from(dr.querySelectorAll("pointref"))
            .map((p) => normalizeXmlAttr(p.getAttribute("name") || ""))
            .map((n) => dim.points.find((pt) => pt.name === n)?.id || "").filter((id): id is string => Boolean(id));

          if (tagIds.length && !pointIds.length) return { dimensionId: dim.id, mode: "tags" as const, tagIds, pointIds: [] };
          if (pointIds.length && !tagIds.length) return { dimensionId: dim.id, mode: "points" as const, tagIds: [], pointIds };
          return { dimensionId: dim.id, mode: "all" as const, tagIds: [], pointIds: [] };
        }).filter((target) => Boolean(target.dimensionId));
      };

      const parseTargetList = (parentSelector: string): RelationTarget[] => {
        const parent = c.querySelector(parentSelector);
        if (!parent) return [];
        return parseDimRefs(Array.from(parent.querySelectorAll("dimref")));
      };

      const parseTargetListByRole = (role: "make" | "for"): RelationTarget[] => {
        const directDimRefs = Array.from(c.children)
          .filter((child): child is Element => child.tagName === "dimref")
          .filter((dr) => dr.getAttribute("role") === role);
        return parseDimRefs(directDimRefs);
      };

      const makeTargets = parseTargetList("make");
      const forTargets = parseTargetList("for");
      const maxAttr = c.getAttribute("maximize");
      const hasMaximize = maxAttr !== null && maxAttr !== "";
      const creationType: Creation["type"] = hasMaximize ? "maximize" : "equal";
      const targetValue = hasMaximize
        ? parseNumberAttr(maxAttr, 1)
        : parseNumberAttr(c.getAttribute("equalValue") || c.getAttribute("value") || c.getAttribute("equal"), 1);

      creations.push({
        id: makeId("creation"),
        name: normalizeXmlAttr(c.getAttribute("name") || `Creation${i + 1}`),
        type: creationType,
        equalValue: targetValue,
        enforcement: (c.getAttribute("enforcement") as Creation["enforcement"]) || "hard",
        connect: (c.getAttribute("connect") || "true") !== "false",
        make: makeTargets.length > 0 ? makeTargets : parseTargetListByRole("make"),
        for: forTargets.length > 0 ? forTargets : parseTargetListByRole("for"),
      });
    });

    /* === Capacities === */
    const capacities: Capacity[] = [];
    doc.querySelectorAll("capacities > capacity").forEach((c, i) => {
      const parseDimRefsByRole = (role: string): RelationTarget[] => {
        // Neues Format: direkte <dimref role="for|where"> Kinder
        const direct = Array.from(c.children)
          .filter((child): child is Element => child.tagName === "dimref" && child.getAttribute("role") === role);
        // Altes Format als Fallback: <resources> oder <area> Wrapper
        if (direct.length === 0) {
          const wrapper = role === "for"
            ? (c.querySelector("resource") || c.querySelector("resources"))
            : c.querySelector("area");
          if (!wrapper) return [];
          return Array.from(wrapper.querySelectorAll("dimref")).map(parseSingleDimRef).filter(Boolean) as RelationTarget[];
        }
        return direct.map(parseSingleDimRef).filter(Boolean) as RelationTarget[];
      };

      const parseSingleDimRef = (dr: Element): RelationTarget | null => {
        const dimName = normalizeXmlAttr(dr.getAttribute("name") || "");
        const dim = dimensions.find((d) => d.name === dimName);
        if (!dim) return null;
        if (dr.querySelector("allpoints")) return { dimensionId: dim.id, mode: "all" as const, tagIds: [], pointIds: [] };
        const tagIds = Array.from(dr.querySelectorAll("tagref"))
          .map((t) => normalizeXmlAttr(t.getAttribute("name") || ""))
          .map((n) => dim.tags.find((tg) => tg.name === n)?.id || "").filter((id): id is string => Boolean(id));
        const pointIds = Array.from(dr.querySelectorAll("pointref"))
          .map((p) => normalizeXmlAttr(p.getAttribute("name") || ""))
          .map((n) => dim.points.find((pt) => pt.name === n)?.id || "").filter((id): id is string => Boolean(id));
        if (tagIds.length && !pointIds.length) return { dimensionId: dim.id, mode: "tags" as const, tagIds, pointIds: [] };
        if (pointIds.length && !tagIds.length) return { dimensionId: dim.id, mode: "points" as const, tagIds: [], pointIds };
        return { dimensionId: dim.id, mode: "all" as const, tagIds: [], pointIds: [] };
      };

      capacities.push({
        id: makeId("cap"),
        name: normalizeXmlAttr(c.getAttribute("name") || `Capacity${i + 1}`),
        type: "limit",
        limitValue: parseNumberAttr(c.getAttribute("limit") || c.getAttribute("limitValue"), 1),
        for: parseDimRefsByRole("for"),
        where: parseDimRefsByRole("where"),
        ...(c.getAttribute("scope") ? { scope: c.getAttribute("scope") } : {}),
        ...(c.getAttribute("mode")  ? { mode:  c.getAttribute("mode")  } : {}),
      } as any);
    });

    /* === Conditions (Extended) === */
    const conditions: ExtendedCondition[] = [];
    doc.querySelectorAll("conditions > condition").forEach((c, i) => {
      const dimrefs: any[] = [];

      const parseDimRefEl = (dr: Element) => {
        const dimName = normalizeXmlAttr(dr.getAttribute("name") || "");
        const dim = dimensions.find((d) => d.name === dimName);
        if (!dim) return null;

        const role = dr.getAttribute("role") as "for" | "where" | "each" | "on" | undefined || undefined;

        if (dr.querySelector("allpoints")) {
          return {
            dimensionId: dim.id,
            role,
            mode: "all",
            tagIds: [],
            pointIds: [],
            id: makeId("dimref"),
          };
        }

        const tagIds = Array.from(dr.querySelectorAll("tagref"))
          .map((t) => normalizeXmlAttr(t.getAttribute("name") || ""))
          .map((n) => dim.tags.find((tg) => tg.name === n)?.id || "")
          .filter(Boolean);

        const pointIds = Array.from(dr.querySelectorAll("pointref"))
          .map((p) => normalizeXmlAttr(p.getAttribute("name") || ""))
          .map((n) => dim.points.find((pt) => pt.name === n)?.id || "")
          .filter(Boolean);

        if (tagIds.length && !pointIds.length) {
          return {
            dimensionId: dim.id,
            role,
            mode: "tags",
            tagIds,
            pointIds: [],
            id: makeId("dimref"),
          };
        }

        if (pointIds.length && !tagIds.length) {
          return {
            dimensionId: dim.id,
            role,
            mode: "points",
            tagIds: [],
            pointIds,
            id: makeId("dimref"),
          };
        }

        // Fallback
        return {
          dimensionId: dim.id,
          role,
          mode: "all",
          tagIds: [],
          pointIds: [],
          id: makeId("dimref"),
        };
      };

      // dimrefs innerhalb einer Condition (nur für nicht-align)
      const isAlign = c.getAttribute("type") === "align";
      if (!isAlign) {
        c.querySelectorAll("dimref").forEach((dr) => {
          const parsed = parseDimRefEl(dr);
          if (parsed) dimrefs.push(parsed);
        });
      }

      // align: participants + on
      let participants: any[] | undefined = undefined;
      let on: any | undefined = undefined;
      if (isAlign) {
        participants = Array.from(c.querySelectorAll("participants > participant")).map((pEl) => {
          const pDimrefs: any[] = [];
          pEl.querySelectorAll(":scope > dimref").forEach((dr) => {
            const parsed = parseDimRefEl(dr);
            if (parsed) pDimrefs.push(parsed);
          });
          return { id: makeId("participant"), dimrefs: pDimrefs };
        });

        // <dimref role="on"> direkt unter <condition>
        const onDimrefs: any[] = [];
        Array.from(c.children)
          .filter((child): child is Element => child.tagName === "dimref" && child.getAttribute("role") === "on")
          .forEach((dr) => {
            const parsed = parseDimRefEl(dr);
            if (parsed) onDimrefs.push(parsed);
          });

        on = { dimrefs: onDimrefs };
      }

      // follow: metric_ref IF/THEN
      let metricRefIf: any = undefined;
      let metricRefThen: any = undefined;
      const isFollow = c.getAttribute("type") === "follow";
      if (isFollow) {
        const metricRefEls = Array.from(c.querySelectorAll(":scope > metric_ref"));
        const patternKeys = ["equal", "not_equal", "min", "max"];
        metricRefEls.forEach((mr) => {
          const role = mr.getAttribute("role");
          const name = normalizeXmlAttr(mr.getAttribute("name") || "");
          let pt = "equal";
          let pv = "";
          for (const pk of patternKeys) {
            const val = mr.getAttribute(pk);
            if (val) {
              pt = pk;
              pv = normalizeXmlAttr(val);
              break;
            }
          }
          const ref = { name, patternType: pt, patternValue: pv };
          if (role === "if") metricRefIf = ref;
          else if (role === "then") metricRefThen = ref;
        });
      }

      // Pattern-Typ und Wert aus Attributen lesen (z.B. equal="5")
      const patternTypes = [
        "equal",
        "not_equal",
        "min",
        "max",
      ];
      let patternType: ExtendedCondition["patternType"] = "equal";
      let patternValue = "";
      for (const pt of patternTypes) {
        const val = c.getAttribute(pt);
        if (val) {
          patternType = pt as ExtendedCondition["patternType"];
          patternValue = normalizeXmlAttr(val);
          break;
        }
      }

      // Gruppen-Name aus XML lesen (wird später zu groupId gemappt)
      const groupName = c.getAttribute("group");

      // Enforcement aus XML lesen (falls vorhanden)
      const enforcementAttr = c.getAttribute("enforcement");
      const enforcement = (enforcementAttr === "hard" || enforcementAttr === "soft" || enforcementAttr === "off")
        ? enforcementAttr
        : undefined;

      // scope + mode
      const scopeAttr = c.getAttribute("scope") || undefined;
      const modeAttr  = c.getAttribute("mode")  || undefined;
      const timeRefAttr = c.getAttribute("time_ref") || undefined;

      conditions.push({
        id: makeId("cond"),
        name: normalizeXmlAttr(c.getAttribute("name") || `Condition${i + 1}`),
        type:
          (c.getAttribute("type") as
            | "count"
            | "distinct"
            | "gap"
            | "block"
            | "align"
            | "follow"
            | "time.sum"
            | "time.gap") || "count",
        enforcement,
        patternType,
        patternValue,
        dimrefs,
        participants,
        on,
        ...(timeRefAttr ? { time_ref: normalizeXmlAttr(timeRefAttr) } : {}),
        groupId: groupName || undefined,
        ...(metricRefIf ? { metricRefIf } : {}),
        ...(metricRefThen ? { metricRefThen } : {}),
        ...(scopeAttr ? { scope: scopeAttr } : {}),
        ...(modeAttr  ? { mode:  modeAttr  } : {}),
      } as ExtendedCondition);
    });

    /* TagRefs in Points nach IDs auflösen (von Namen → Tag-ID) */
    dimensions.forEach((d) => {
      d.points.forEach((p) => {
        p.tagRefs = p.tagRefs
          .map((refName) => d.tags.find((t) => t.name === refName)?.id || "")
          .filter(Boolean);
      });
    });

    /* Gruppen aus Conditions extrahieren (Reihenfolge beibehalten) */
    const groupNamesInOrder: string[] = [];
    conditions.forEach((cond) => {
      const groupName = cond.groupId as string;
      if (groupName && groupNamesInOrder.indexOf(groupName) === -1) {
        groupNamesInOrder.push(groupName);
      }
    });

    // ConditionGroups aus Gruppennamen bauen
    const conditionGroups: ConditionGroup[] = [];
    groupNamesInOrder.forEach((name) => {
      // Spezielle Behandlung für bekannte Namen
      if (name === "Standard") {
        conditionGroups.push({
          id: "default",
          name: "Standard",
          enabled: true,
        });
      } else if (name === "Bibliothek") {
        conditionGroups.push({
          id: "library",
          name: "Bibliothek",
          enabled: false,
        });
      } else {
        conditionGroups.push({
          id: makeId("group"),
          name,
          enabled: true,
        });
      }
    });

    // Falls keinerlei Gruppen in XML vorhanden waren -> eine Default-Gruppe
    if (conditionGroups.length === 0) {
      conditionGroups.push({
        id: "group-default",
        name: "Group1",
        enabled: true,
      });
    }

    // Conditions: groupName → groupId zuordnen (Fallback: erste Gruppe)
    const fallbackGroupId = conditionGroups[0]?.id || "group-default";
    conditions.forEach((cond) => {
      const groupName = cond.groupId as string;
      if (groupName) {
        const group = conditionGroups.find((g) => g.name === groupName);
        cond.groupId = group ? group.id : fallbackGroupId;
      } else {
        cond.groupId = fallbackGroupId;
      }
    });

    // Fertiges DataModel zurückgeben
    // ---------- Adjustments ----------
    const adjustments: Adjustment[] = [];
    doc.querySelectorAll("adjustments > adjustment").forEach((a, i) => {
      const type = (a.getAttribute("type") || "off") as "off" | "pin";
      const name = normalizeXmlAttr(a.getAttribute("name") || `Adjustment${i + 1}`);
      const startDate = a.getAttribute("start_date") || undefined;
      const endDate   = a.getAttribute("end_date")   || undefined;
      const date      = a.getAttribute("date")        || undefined;

      const targets = Array.from(a.querySelectorAll("dimref")).map((dr) => {
        const dimName = normalizeXmlAttr(dr.getAttribute("name") || "");
        const dim = dimensions.find((d) => d.name === dimName);
        if (!dim) return { dimensionId: "", mode: "all" as const, tagIds: [], pointIds: [] };

        if (dr.querySelector("allpoints")) {
          return { dimensionId: dim.id, mode: "all" as const, tagIds: [], pointIds: [] };
        }
        const tagIds = Array.from(dr.querySelectorAll("tagref"))
          .map((t) => normalizeXmlAttr(t.getAttribute("name") || ""))
          .map((n) => dim.tags.find((tg) => tg.name === n)?.id || "")
          .filter(Boolean);
        const pointIds = Array.from(dr.querySelectorAll("pointref"))
          .map((p) => normalizeXmlAttr(p.getAttribute("name") || ""))
          .map((n) => dim.points.find((pt) => pt.name === n)?.id || "")
          .filter(Boolean);
        if (tagIds.length && !pointIds.length)
          return { dimensionId: dim.id, mode: "tags" as const, tagIds, pointIds: [] };
        if (pointIds.length)
          return { dimensionId: dim.id, mode: "points" as const, tagIds: [], pointIds };
        return { dimensionId: dim.id, mode: "all" as const, tagIds: [], pointIds: [] };
      });

      adjustments.push({ id: makeId("adj"), name, type, startDate, endDate, date, targets });
    });

    // ---------- Metrics ----------
    const metrics: Metric[] = [];
    doc.querySelectorAll("metrics > metric").forEach((m, i) => {
      const metricTypeRaw = m.getAttribute("type") || "count";
      const metricType: Metric["type"] =
        metricTypeRaw === "time.sum" ? "time.sum" : "count";

      const dimrefs: Metric["dimrefs"] = [];
      m.querySelectorAll("dimref").forEach((dr) => {
        const dimName = normalizeXmlAttr(dr.getAttribute("name") || "");
        const dim = dimensions.find((d) => d.name === dimName);
        if (!dim) return;

        const roleRaw = dr.getAttribute("role") || "for";
        if (roleRaw !== "for" && roleRaw !== "where") return;
        const role: Metric["dimrefs"][number]["role"] = roleRaw;

        const tagIds = Array.from(dr.querySelectorAll("tagref"))
          .map((t) => normalizeXmlAttr(t.getAttribute("name") || ""))
          .map((n) => dim.tags.find((tg) => tg.name === n)?.id || "")
          .filter(Boolean);
        const pointIds = Array.from(dr.querySelectorAll("pointref"))
          .map((p) => normalizeXmlAttr(p.getAttribute("name") || ""))
          .map((n) => dim.points.find((pt) => pt.name === n)?.id || "")
          .filter(Boolean);
        const mode = tagIds.length && !pointIds.length ? "tags" : pointIds.length && !tagIds.length ? "points" : "all";
        dimrefs.push({ id: makeId("dimref"), dimensionId: dim.id, role, mode, tagIds, pointIds });
      });

      metrics.push({
        id: makeId("metric"),
        name: normalizeXmlAttr(m.getAttribute("name") || `Metric${i + 1}`),
        type: metricType,
        ...(m.getAttribute("time_ref") ? { time_ref: normalizeXmlAttr(m.getAttribute("time_ref") || "") } : {}),
        dimrefs,
      });
    });

    // ---------- Objectives ----------
    const objectives: Objective[] = [];
    doc.querySelectorAll("objectives > objective").forEach((l, i) => {
      const objName = normalizeXmlAttr(l.getAttribute("name") || `Objective${i + 1}`);
      const typeRaw = l.getAttribute("type") || "target";
      const validTypes = ["minimize", "maximize", "target", "balance"];
      const type = (validTypes.includes(typeRaw) ? typeRaw : "target") as Objective["type"];
      const metric_ref = normalizeXmlAttr(l.getAttribute("metric_ref") || "");
      const target = l.getAttribute("target") ? normalizeXmlAttr(l.getAttribute("target") || "") : undefined;

      objectives.push({
        id: makeId("objective"),
        name: objName,
        type,
        metric_ref,
        target,
      });
    });

    // ---------- Fixations ----------
    const fixations: Fixation[] = [];
    doc.querySelectorAll("fixations > fixation").forEach((f, i) => {
      const name = normalizeXmlAttr(f.getAttribute("name") || `Fixation${i + 1}`);
      const targets = Array.from(f.querySelectorAll("dimref")).map((dr) => {
        const dimName = normalizeXmlAttr(dr.getAttribute("name") || "");
        const dim = dimensions.find((d) => d.name === dimName);
        if (!dim) return { dimensionId: "", mode: "all" as const, tagIds: [], pointIds: [] };
        if (dr.querySelector("allpoints")) {
          return { dimensionId: dim.id, mode: "all" as const, tagIds: [], pointIds: [] };
        }
        const tagIds = Array.from(dr.querySelectorAll("tagref"))
          .map((t) => normalizeXmlAttr(t.getAttribute("name") || ""))
          .map((n) => dim.tags.find((tg) => tg.name === n)?.id || "")
          .filter(Boolean);
        const pointIds = Array.from(dr.querySelectorAll("pointref"))
          .map((p) => normalizeXmlAttr(p.getAttribute("name") || ""))
          .map((n) => dim.points.find((pt) => pt.name === n)?.id || "")
          .filter(Boolean);
        if (tagIds.length && !pointIds.length)
          return { dimensionId: dim.id, mode: "tags" as const, tagIds, pointIds: [] };
        if (pointIds.length)
          return { dimensionId: dim.id, mode: "points" as const, tagIds: [], pointIds };
        return { dimensionId: dim.id, mode: "all" as const, tagIds: [], pointIds: [] };
      });
      fixations.push({ id: makeId("fix"), name, targets,
      });
    });

    // ---------- Timings ----------
    const timings: Timing[] = [];
    doc.querySelectorAll("timings > timing").forEach((t, i) => {
      const name = normalizeXmlAttr(t.getAttribute("name") || `timing${i + 1}`);
      const type = (t.getAttribute("type") || "relative") as Timing["type"];
      if (type === "relative") {
        timings.push({
          id: makeId("timing"),
          name,
          type: "relative",
          anchor: (t.getAttribute("anchor") as Timing["anchor"]) || undefined,
          direction: (t.getAttribute("direction") as Timing["direction"]) || undefined,
          length: t.getAttribute("length") || undefined,
          unit: (t.getAttribute("unit") as Timing["unit"]) || undefined,
          ...(t.getAttribute("offset") != null && t.getAttribute("offset") !== ""
            ? { offset: parseInt(t.getAttribute("offset")!, 10) }
            : {}),
        });
      } else {
        timings.push({
          id: makeId("timing"),
          name,
          type: "absolute",
          start_date: t.getAttribute("start_date") || undefined,
          end_date: t.getAttribute("end_date") || undefined,
        });
      }
    });

    return {
      settings,
      dimensions,
      timings,
      relations,
      creations,
      capacities,
      conditions,
      conditionGroups,
      metrics,
      objectives: objectives,
      fixations,
      adjustments,
      rules_xml: text,
      plan_csv: "",
      fulfillments_json: null,
      syntaxchecks_json: null,
    } as DataModel;
  } catch (e) {
    console.error("xmlToData error:", e);
    return null;
  }
}
